/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer, useState } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000DefaultForm from '../../../u2000Core/components/formControls/U2000DefaultForm';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3303ROLES } from '../../U3303';
import { U3303HolidayYearFormToolBar } from './U3303HolidayYearFormToolBar';
import U3303HolidayYearBody from './U3303HolidayYearBody';
import U3303HolidayYearDeleteDlg from '../common/U3303HolidayYearDeleteDlg';
import { useU3303Context } from '../../U3303Context';
import U2000AuditPanelComponent from '../../../u2000Core/components/audit/U2000AuditPanelComponent';
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  createEmptyU3303FormRow,
  doCreateHolidayYear,
  doDeleteHolidayYear,
  doGetHolidayYear,
  doGetHolidayYearAudits,
  doSearchHolidayYears,
  doUpdateHolidayYear,
  formToSearchRow,
  luHandleAppiError,
  normalizeU3303FromApi,
  validateU3303FormRow,
  type U3303HolidayYearFormRow,
  type U3303HolidayYearGet,
  type U3303HolidayYearSearchRow,
} from '../../U3303Service';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../../u2000Core/components/tab/U2000Tab';

export interface U3303HolidayYearFormExtendedHandlers extends U2000FormStateHandlersReturn<U3303HolidayYearFormRow> {
  addNewHolidayYear: () => void;
  modifyHolidayYear: () => void;
  deleteHolidayYear: () => Promise<void>;
}

const U3303HolidayYearForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3303Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const translateValidationErrors = (raw: Record<string, string>): Record<string, string> => {
    const out: Record<string, string> = {};
    Object.entries(raw).forEach(([field, key]) => {
      out[field] = t(key, { ns: NS });
    });
    return out;
  };

  const validateForm = (row: U3303HolidayYearFormRow) => translateValidationErrors(validateU3303FormRow(row));

  const u3303BL: U2000FormSBusinessHandlers<U3303HolidayYearFormRow> = {
    newRecord: () => createEmptyU3303FormRow(),
    validateForm,
    saveForme: async (row: U3303HolidayYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3303FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },
    cancelForm: () => {
      const old = u3303FormExtended.getOldData() as U3303HolidayYearFormRow | null;
      if (old) u3303FormExtended.setData({ ...old });
      if (luStateHandlers.getActionOriginFrom() === '/U3303') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) handleGet.getData(row.id);
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => {
      navigate('/U3303');
    },
  };

  const u3303FormHandlers = useU2000FormStateHandlers<U3303HolidayYearFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3303BL,
  });

  const u3303FormExtended: U3303HolidayYearFormExtendedHandlers = {
    ...u3303FormHandlers,
    addNewHolidayYear: () => {
      u3303FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3303FormExtended.setData(createEmptyU3303FormRow());
      navigate('/U3303/new', { replace: true });
    },
    modifyHolidayYear: () => {
      u3303FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteHolidayYear: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3303HolidayYearSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => doSearchHolidayYears(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3303HolidayYearSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3303HolidayYearGet>({
    doGetFn: (rowId: string) => doGetHolidayYear(u2000AxiosAuthenticated, rowId),
    onSuccess: (row: U3303HolidayYearGet) => {
      const normalized = normalizeU3303FromApi(row);
      gridStateHandlers.setDataById(normalized.id, formToSearchRow(normalized));
      u3303FormExtended.setData(normalized);
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([
        { href: '/U3303', label: listTitle },
        { label: String(normalized.year) },
      ]);
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3303FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3303HolidayYearFormRow>({
    doSaveFn: (row: U3303HolidayYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateHolidayYear(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateHolidayYear(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (apiRow: U3303HolidayYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeU3303FromApi(apiRow as unknown as U3303HolidayYearGet);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), formToSearchRow(normalized)]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        navigate(`/U3303/${normalized.id}`, { replace: true });
      } else {
        gridStateHandlers.setDataById(normalized.id, formToSearchRow(normalized));
      }
      u3303FormExtended.afterSaveData(normalized);
      u3303FormExtended.setData(normalized);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));
      if (
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY
      ) {
        if (luStateHandlers.getActionOriginFrom() === '/U3303') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3303FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => {
      const data = u3303FormExtended.getData();
      return data ? [formToSearchRow(data)] : [];
    },
    doDeleteFn: async (_rowId) => {
      const data = u3303FormExtended.getData();
      if (!data) return;
      return await doDeleteHolidayYear(u2000AxiosAuthenticated, buildDeleteBody(data));
    },
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((del) => {
        toast.success(t('success0003', { key: String((del as U3303HolidayYearSearchRow).year), ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3303', { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      u3303FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3303FormExtended.setData(createEmptyU3303FormRow());
      setLogicalUnitBreadcrumb([
        { href: '/U3303', label: listTitle },
        { label: t('btnAdd', { ns: NS }) },
      ]);
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  const data = u3303FormExtended.getData();
  if (data === null) return null;

  const formTabs = [
    data.id &&
      hasRole(U3303ROLES.AUDITS) && {
        id: 'tabAudits',
        label: t('tabAudits', { ns: NS }),
        content: (
          <U2000AuditPanelComponent
            id={data.id}
            doGetAuditsData={doGetHolidayYearAudits}
            toastName="U3303HolidayYearAudit"
          />
        ),
      },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3303HolidayYearDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U3303HolidayYearFormToolBar formStateHandlers={u3303FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3303FormExtended as any}
      >
        <U3303HolidayYearBody formStateHandlers={u3303FormExtended as any} t={t} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            activeTab={activeTab ?? ''}
            setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3303HolidayYearForm;
